import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useQueryClient } from "react-query";
import dayjs from "dayjs";
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem,
    Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LinkIcon from "@mui/icons-material/Link";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { shortImei } from "../../components/pedal/PedalBoard";
import { getErrorMessage } from "../../services/Client";
import {
    createPedalShareLink, PEDAL_SHARE_LINK, revokePedalShareLink, useGetPedalShareLink
} from "../../services/UserDataManager";

const DURATIONS = ["DAY", "WEEK", "MONTH"];
const RADIUS = "14px";
const BORDER = "#e5e7eb";
// sotto questa soglia il token e' "in scadenza"
const EXPIRING_MS = 24 * 3600 * 1000;

const fmt = ms => dayjs(new Date(ms)).format("DD/MM/YYYY HH:mm");

/**
 * Token di accesso (scheda della pagina del pinbiker, come "Gestione dei token" della Web Board): il token
 * e' quello del link condivisibile del pedale, che apre la pagina pubblica /monitor/{token} o si inserisce
 * nel login. Uno solo attivo per volta (regola del backend): "Nuovo token" e' attivo solo se non ce n'e'.
 * Revocare cancella il token; quelli scaduti li toglie il backend, quindi l'elenco ha al piu' una riga.
 * Il token riguarda un solo pedale, scelto alla creazione: una mappa con piu' pedali disorienta chi apre il
 * link, che di solito sono le forze dell'ordine.
 */
export default function PinbikerTokens({ brand, hasPedal, pedals = [] }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const { link, status } = useGetPedalShareLink();
    const [open, setOpen] = useState(false);
    const [imei, setImei] = useState("");
    const [duration, setDuration] = useState("DAY");
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);

    const refresh = () => queryClient.invalidateQueries([PEDAL_SHARE_LINK]);
    const token = link ? link.url.split("/").pop() : null;

    const create = () => {
        setBusy(true);
        createPedalShareLink({ imei, duration, label })
            .then(() => {
                setOpen(false);
                setLabel("");
            })
            .catch(e => enqueueSnackbar(t(getErrorMessage(e)), { variant: "error" }))
            .finally(() => {
                setBusy(false);
                refresh();
            });
    };

    const revoke = () => {
        setBusy(true);
        revokePedalShareLink()
            .catch(e => enqueueSnackbar(getErrorMessage(e), { variant: "error" }))
            .finally(() => {
                setBusy(false);
                refresh();
            });
    };

    const copy = (text, message) => navigator.clipboard.writeText(text)
        .then(() => enqueueSnackbar(message, { variant: "success" }));

    const expiring = link && Date.parse(link.expiresAt) - Date.now() < EXPIRING_MS;
    const message = link ? encodeURIComponent(`${t('sharePedalMessage')} ${link.url}`) : "";

    return (
        <Box>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
                alignItems={{ xs: "stretch", sm: "flex-start" }} spacing={2} sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box sx={{ bgcolor: "#eef2f7", borderRadius: "10px", p: 1, display: "flex" }}>
                        <KeyIcon sx={{ color: brand.color }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{t('loginTabCode')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('accessTokensSubtitle')}</Typography>
                    </Box>
                </Stack>
                <Tooltip title={link ? t('oneActiveToken') : !hasPedal ? t('noPedalPosition') : ""}>
                    <span>
                        <Button variant="contained" disableElevation startIcon={<AddIcon />}
                            disabled={!!link || !hasPedal || busy}
                            onClick={() => { setImei(pedals[0] ? pedals[0].imei : ""); setOpen(true); }}
                            sx={{
                                textTransform: "none", fontWeight: 600, borderRadius: "10px", bgcolor: brand.color,
                                color: brand.contrastText, "&:hover": { bgcolor: brand.color, filter: "brightness(0.92)" },
                            }}>
                            {t('newToken')}
                        </Button>
                    </span>
                </Tooltip>
            </Stack>

            <Box sx={{ bgcolor: "#fff", border: `1px solid ${BORDER}`, borderRadius: RADIUS, overflow: "hidden" }}>
                {status === "loading"
                    ? <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
                    : <TableContainer>
                        <Table sx={{
                            minWidth: 720,
                            "& th": {
                                color: "text.secondary", fontSize: 12, fontWeight: 600, textTransform: "uppercase",
                                letterSpacing: ".04em", bgcolor: "#fafafb",
                            },
                        }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Token</TableCell>
                                    <TableCell>{t('pedal')}</TableCell>
                                    <TableCell>{t('name')}</TableCell>
                                    <TableCell>{t('creationDate')}</TableCell>
                                    <TableCell>{t('tokenExpiry')}</TableCell>
                                    <TableCell>{t('status')}</TableCell>
                                    <TableCell align="right">{t('tokenActions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!link
                                    ? <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                                            {t('noActiveToken')}
                                        </TableCell>
                                    </TableRow>
                                    : <TableRow>
                                        <TableCell>
                                            <Box sx={{
                                                display: "inline-flex", alignItems: "center", gap: 0.5, border: `1px solid ${BORDER}`,
                                                borderRadius: "8px", pl: 1.25, bgcolor: "#fafafb",
                                            }}>
                                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{token}</Typography>
                                                <Tooltip title={t('copy')}>
                                                    <IconButton size="small" onClick={() => copy(token, t('tokenCopied'))}>
                                                        <ContentCopyIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell>{link.imei ? `UGO-${shortImei(link.imei)}` : "-"}</TableCell>
                                        <TableCell>{link.label || "-"}</TableCell>
                                        <TableCell>{fmt(link.createdAt)}</TableCell>
                                        <TableCell sx={{ color: expiring ? "#b45309" : "inherit" }}>{fmt(link.expiresAt)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={t(expiring ? 'tokenExpiring' : 'active')}
                                                sx={expiring
                                                    ? { bgcolor: "#fef3c7", color: "#b45309", fontWeight: 600 }
                                                    : { bgcolor: "#ecf8e3", color: "#3f7d1d", fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title={t('linkCopied')}>
                                                <IconButton size="small" onClick={() => copy(link.url, t('linkCopied'))}><LinkIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="WhatsApp">
                                                <IconButton size="small" href={`https://wa.me/?text=${message}`} target="_blank"
                                                    rel="noopener noreferrer"><WhatsAppIcon fontSize="small" /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Email">
                                                <IconButton size="small"
                                                    href={`mailto:?subject=${encodeURIComponent(t('sharePedalPosition'))}&body=${message}`}>
                                                    <EmailIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('revoke')}>
                                                <span>
                                                    <IconButton size="small" disabled={busy} onClick={revoke}><DeleteIcon fontSize="small" /></IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>}
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { borderRadius: RADIUS } }}>
                <DialogTitle>{t('newToken')}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('sharePedalWarning')}</Typography>
                    <TextField select fullWidth label={t('pedal')} value={imei}
                        onChange={e => setImei(e.target.value)} sx={{ mb: 2 }}>
                        {pedals.map(p => <MenuItem key={p.imei} value={p.imei}>UGO-{shortImei(p.imei)}</MenuItem>)}
                    </TextField>
                    <TextField select fullWidth label={t('shareDuration')} value={duration}
                        onChange={e => setDuration(e.target.value)} sx={{ mb: 2 }}>
                        {DURATIONS.map(d => <MenuItem key={d} value={d}>{t(`shareDuration${d}`)}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth label={t('shareLabel')} value={label} inputProps={{ maxLength: 80 }}
                        onChange={e => setLabel(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>{t('cancel')}</Button>
                    <Button variant="contained" disableElevation disabled={busy || !imei}
                        onClick={create}>{t('newToken')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
